import React, { useState } from "react";
import { useParams, Link } from "react-router-dom";
import {
  ChevronLeft, Instagram, Facebook, Linkedin, CheckCircle2, AlertTriangle, Clock, Check,
} from "lucide-react";
import OpsTopBar from "../../components/ops/OpsTopBar";
import { BranchAvatar, OperatorAvatar } from "../../components/ops/BranchAvatar";
import AllowanceBar from "../../components/ops/AllowanceBar";
import { StatusPill, PlatformIcon, FormatTag } from "../../components/ops/Pills";
import useApi from "../../hooks/useApi";

const TABS = ["Overview", "Calendar", "Library", "History"];
const PLAT_ICON = { Instagram, Facebook, LinkedIn: Linkedin };
const DAY_LABEL = { 0: "Today", 1: "Tomorrow", 2: "In 2 days", 3: "In 3 days", 4: "In 4 days" };

export default function OpsBranch() {
  const { id } = useParams();
  const [tab, setTab] = useState("Overview");
  const { data } = useApi(`/ops/branches/${id}`, [id]);
  const b = data?.branch;
  const op = data?.operator;
  if (!b) return (<><OpsTopBar title="Branch" /><div className="p-8 text-ink-2">Loading branch…</div></>);

  const calByDay = {};
  (data.calendar || []).forEach((t) => { (calByDay[t.day_offset] = calByDay[t.day_offset] || []).push(t); });
  const libByCat = {};
  (data.library || []).forEach((l) => { (libByCat[l.category] = libByCat[l.category] || []).push(l); });

  return (
    <>
      <OpsTopBar title={`${b.group_name} — ${b.name}`} subtitle={b.handle} />
      <div className="p-6 sm:p-8 max-w-[1300px] space-y-6">
        <Link to="/ops/branches" className="inline-flex items-center gap-1.5 text-sm text-ink-2 hover:text-ink" data-testid="branch-back"><ChevronLeft size={15} /> All branches</Link>

        {/* Header */}
        <div className="card p-6" data-testid="branch-header">
          <div className="flex flex-col sm:flex-row gap-5 sm:items-center">
            <BranchAvatar initials={b.initials} color={b.color} size={60} />
            <div className="flex-1">
              <h2 className="text-2xl font-extrabold tracking-tight text-ink">{b.name}</h2>
              <div className="text-sm text-ink-2">{b.group_name} · {b.handle}</div>
              <div className="inline-flex items-center gap-2 mt-2 rounded-full px-3 py-1" style={{ background: "var(--grad-green-soft)" }}>
                <span className="w-2 h-2 rounded-full" style={{ background: "#93C020" }} /><span className="text-xs font-semibold" style={{ color: "#5F8213" }}>Managed · {b.managed_status}</span>
              </div>
            </div>
            <div className="sm:w-80"><AllowanceBar allowance={b.allowance} reset={b.allowance_reset} size="lg" /></div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 border-b" style={{ borderColor: "#E7E8E4" }} data-testid="branch-tabs">
          {TABS.map((t) => (
            <button key={t} onClick={() => setTab(t)} data-testid={`tab-${t.toLowerCase()}`}
              className="px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px transition-colors"
              style={tab === t ? { borderColor: "#93C020", color: "#16180F" } : { borderColor: "transparent", color: "#9A9C90" }}>{t}</button>
          ))}
        </div>

        {tab === "Overview" && (
          <div className="grid lg:grid-cols-2 gap-5" data-testid="branch-overview">
            <div className="card p-5">
              <span className="label-caps">Social accounts</span>
              <div className="space-y-3 mt-3">
                {(b.accounts || []).map((a) => {
                  const Icon = PLAT_ICON[a.platform] || Instagram;
                  return (
                    <div key={a.platform} className="flex items-center gap-3 rounded-xl p-3" style={{ background: "#F6F8F1" }} data-testid={`account-${a.platform}`}>
                      <span className="grid place-items-center w-9 h-9 rounded-lg bg-white border" style={{ borderColor: "#E7E8E4" }}><Icon size={16} className="text-ink-2" /></span>
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-ink text-sm">{a.handle}</div>
                        <div className="text-xs text-ink-3">{a.method}{a.last_activity ? ` · Last activity ${a.last_activity}` : ""}</div>
                      </div>
                      {a.issue
                        ? <span className="flex items-center gap-1 text-xs font-semibold" style={{ color: "#D0526B" }}><AlertTriangle size={13} /> {a.issue}</span>
                        : <span className="flex items-center gap-1 text-xs font-semibold" style={{ color: "#5F8213" }}><CheckCircle2 size={13} /> {a.api_publish ? "API" : "Manual"}</span>}
                    </div>
                  );
                })}
              </div>
            </div>
            <div className="space-y-5">
              <div className="card p-5">
                <span className="label-caps">Assigned operator</span>
                <div className="flex items-center gap-3 mt-3">
                  {op && <OperatorAvatar initials={op.initials} color={op.color} size={40} />}
                  <div><div className="font-semibold text-ink">{op?.name || "Unassigned"}</div><div className="text-xs text-ink-3 capitalize">{op?.role?.replace("_", " ")}</div></div>
                </div>
              </div>
              <div className="card p-5">
                <span className="label-caps">Commercials</span>
                <div className="grid grid-cols-2 gap-4 mt-3">
                  <div><div className="text-2xl font-extrabold tracking-tighter text-ink">£{b.managed_price}</div><div className="text-xs text-ink-2">Managed / month</div></div>
                  <div><div className="text-2xl font-extrabold tracking-tighter text-ink">{b.allowance.total}</div><div className="text-xs text-ink-2">Actions included</div></div>
                </div>
              </div>
            </div>
          </div>
        )}

        {tab === "Calendar" && (
          <div className="space-y-5" data-testid="branch-calendar">
            {Object.keys(calByDay).sort((a, c) => a - c).map((d) => (
              <div key={d}>
                <div className="text-sm font-bold text-ink mb-2">{DAY_LABEL[d] || `In ${d} days`}</div>
                <div className="card overflow-hidden divide-y" style={{ borderColor: "#E7E8E4" }}>
                  {calByDay[d].map((t) => (
                    <Link key={t.id} to={`/ops/task/${t.id}`} className="flex items-center gap-3 px-4 py-3 hover:bg-[#F6F8F1]" data-testid={`cal-item-${t.id}`}>
                      <span className="flex items-center gap-1.5 text-xs font-semibold text-ink-2 w-16"><Clock size={12} /> {t.recommended_time}</span>
                      <FormatTag format={t.format} />
                      <span className="text-sm font-medium text-ink flex-1 truncate">{t.reason} · {t.property?.address}</span>
                      <StatusPill status={t.status} />
                    </Link>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === "Library" && (
          <div className="space-y-5" data-testid="branch-library">
            {TABS && Object.keys(libByCat).map((cat) => (
              <div key={cat}>
                <div className="text-sm font-bold text-ink mb-2">{cat} <span className="text-ink-3 font-normal">· {libByCat[cat].length}</span></div>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {libByCat[cat].map((l) => (
                    <div key={l.id} className="card p-3 flex gap-3" data-testid={`lib-${l.id}`}>
                      <img src={l.image} alt="" className="w-14 h-14 rounded-lg object-cover shrink-0" />
                      <div className="min-w-0">
                        <div className="font-semibold text-ink text-sm truncate">{l.title}</div>
                        <div className="text-xs text-ink-3 truncate">{l.detail}</div>
                        {l.used ? (
                          <div className="mt-1">
                            <span className="text-[11px] font-bold uppercase tracking-wide rounded-full px-2 py-0.5" style={{ background: "rgba(147,192,32,.15)", color: "#5F8213" }}>Published {l.used_count}×</span>
                            <div className="text-[11px] text-ink-3 mt-1">{l.usage.map((u) => `${u.platform} — ${u.date}`).join(" · ")}</div>
                          </div>
                        ) : <span className="inline-block mt-1 text-[11px] font-bold uppercase tracking-wide rounded-full px-2 py-0.5" style={{ background: "#EDEEE9", color: "#6B6E62" }}>Not used</span>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === "History" && (
          <div className="card overflow-hidden" data-testid="branch-history">
            <div className="hidden sm:grid grid-cols-12 px-5 py-2.5 label-caps border-b" style={{ borderColor: "#E7E8E4" }}>
              <span className="col-span-2">Date</span><span className="col-span-4">Content</span><span className="col-span-2">Property</span><span className="col-span-2">Operator</span><span className="col-span-1">Method</span><span className="col-span-1 text-right">Status</span>
            </div>
            <div className="divide-y" style={{ borderColor: "#E7E8E4" }}>
              {(data.history || []).map((h) => (
                <div key={h.id} className="grid grid-cols-12 items-center px-5 py-3 gap-2 text-sm" data-testid={`history-${h.id}`}>
                  <span className="col-span-4 sm:col-span-2 text-ink-2">{h.date} · {h.time}</span>
                  <span className="col-span-8 sm:col-span-4 font-medium text-ink truncate">{h.format}</span>
                  <span className="col-span-6 sm:col-span-2 text-ink-2 truncate">{h.property}</span>
                  <span className="col-span-3 sm:col-span-2 text-ink-2 truncate">{h.operator}</span>
                  <span className="col-span-2 sm:col-span-1 text-ink-2">{h.method}</span>
                  <span className="col-span-1 flex justify-end"><span className="flex items-center gap-1 text-xs font-semibold" style={{ color: "#5F8213" }}><Check size={13} strokeWidth={2.5} /></span></span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </>
  );
}
